<template>
  <section
    id="contact-form"
    class="contact-form-module-component">
    <div class="inner-container">
      <div class="form-inner">
        <div class="form-group">
          <h2
            v-if="data.form_name"
            class="form-section-title"
            v-html="data.form_name"></h2>

          <div
            v-if="isLoading"
            class="form-status"
            role="status">
            Loading form…
          </div>

          <FormKit
            v-else-if="form && isFormVisible"
            id="gravity-form"
            type="form"
            :actions="false"
            :incomplete-message="false"
            @submit="submitHandler"
            @submit-invalid="showErrors">
            <FormKitSchema :schema="selectedForm" />
          </FormKit>

          <div
            v-else-if="loadError"
            class="form-status form-error"
            role="alert">
            {{ loadError }}
          </div>

          <div
            v-if="messages.length"
            class="form-response"
            :class="{ error: !isSuccessful }"
            :role="isSuccessful ? 'status' : 'alert'"
            v-html="messages.join('<br>')"></div>

          <button
            v-if="!isFormVisible"
            class="submit-another-button"
            type="button"
            @click="showFormAgain">
            Submit another response
          </button>
        </div>

        <div
          v-if="data.add_contact_cards && data.contact_cards?.length"
          class="contact-cards">
          <article
            v-for="(card, index) in data.contact_cards"
            :key="`contact-card-${index}`"
            class="contact-card">
            <div
              v-if="card.title"
              class="contact-card-title"
              v-html="card.title"></div>

            <div class="contact-bottom">
              <div
                v-if="card.contact_info"
                class="contact-card-info"
                v-html="card.contact_info"></div>

              <a
                v-if="card.button?.url"
                class="contact-card-button"
                :href="card.button.url"
                :target="card.button.target || '_self'">
                {{ card.button.title }}
              </a>
            </div>
          </article>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeMount, ref } from 'vue'
import { FormKitSchema } from '@formkit/vue'
import { reset } from '@formkit/core'
import Axios from 'axios'
import useSmoothScroll from '@/components/composables/useSmoothScroll'

const props = defineProps(['data'])

const disabled = ref(false)
const isFormVisible = ref(true)
const messages = ref([])
const isSuccessful = ref(true)
const form = ref(null)
const isLoading = ref(false)
const loadError = ref('')
const countriesList = ref([])
const { locoScroll } = useSmoothScroll()

const siteUrl =
  window.__VUE_WORDPRESS__?.state?.site?.url || window.location.origin

const selectOptions = (choices = []) =>
  choices.map((choice) => ({
    label: choice.text,
    value: choice.value ?? choice.text,
  }))

const validationTypes = (required, type, maxLength) => {
  const validation = []

  if (required) validation.push('required')
  if (type === 'email') validation.push('email')
  if (type === 'textarea' && Number(maxLength) > 0) {
    validation.push(`length:0,${maxLength}`)
  }

  return validation.join('|')
}

const gfInputName = (inputId) => `input_${String(inputId).replace('.', '_')}`

const gfSubInputName = (field, subInputNumber) => {
  const input = Array.isArray(field.inputs)
    ? field.inputs.find(
        (item) => String(item.id).split('.').pop() === String(subInputNumber)
      )
    : null

  return gfInputName(input?.id ?? `${field.id}.${subInputNumber}`)
}

const fieldClass = (field, extraClass = '') =>
  [
    'formkit-outer',
    field.cssClass,
    field.isRequired ? 'required' : '',
    extraClass,
  ]
    .filter(Boolean)
    .join(' ')

const consentFieldSchema = (field) => {
  const checkboxLabel = field.checkboxLabel || field.label
  const descriptionId = `gfield_consent_description_${
    form.value?.id || field.formId || props.data.form_id
  }_${field.id}`
  const children = []

  if (field.description) {
    children.push({
      $el: 'div',
      attrs: {
        id: descriptionId,
        class: 'gfield_consent_description',
        tabindex: '0',
        innerHTML: field.description,
      },
    })
  }

  children.push(
    {
      $formkit: 'checkbox',
      name: gfSubInputName(field, 1),
      label: checkboxLabel,
      onValue: '1',
      offValue: '',
      validation: validationTypes(field.isRequired, 'checkbox'),
      classes: {
        outer: fieldClass(field, 'consent-checkbox'),
      },
    },
    {
      $formkit: 'hidden',
      name: gfSubInputName(field, 2),
      value: checkboxLabel,
    },
    {
      $formkit: 'hidden',
      name: gfSubInputName(field, 3),
      value: form.value?.latestFormRevisionId || '',
    }
  )

  return {
    $el: 'div',
    attrs: {
      class: fieldClass(field, 'consent-field'),
    },
    children,
  }
}

const getDefaultCountry = (field, options) => {
  const countryInput = Array.isArray(field?.inputs)
    ? field.inputs.find((input) =>
        /country/i.test(String(input.label || input.name || ''))
      )
    : null
  const defaultCountry =
    field?.defaultValue ||
    field?.default_country ||
    countryInput?.defaultValue ||
    ''

  return options.includes(defaultCountry) ? defaultCountry : ''
}

const addressCountrySchema = (field) => {
  const countryInput = Array.isArray(field.inputs)
    ? field.inputs.find((input) =>
        /country/i.test(String(input.label || input.name || ''))
      )
    : null
  const inputName = countryInput?.id
    ? gfInputName(countryInput.id)
    : `input_${field.id}_6`

  return {
    $formkit: 'select',
    name: inputName,
    label: countryInput?.customLabel || countryInput?.label || 'Country',
    placeholder: 'Select country',
    options: countriesList.value,
    value: getDefaultCountry(field, countriesList.value),
    validation: validationTypes(field.isRequired, 'select'),
    classes: {
      outer: fieldClass(field),
    },
  }
}

const selectedForm = computed(() => {
  const needsOptions = new Set(['select', 'radio', 'checkbox', 'multiselect'])
  const fields = (form.value?.fields ?? [])
    .filter((field) => field.type !== 'submit')
    .map((field) => {
      if (field.type === 'consent') return consentFieldSchema(field)
      if (field.type === 'address') return addressCountrySchema(field)

      if (field.type === 'html') {
        return {
          $el: 'div',
          attrs: {
            class: fieldClass(field, 'form-html'),
            innerHTML: field.content || '',
          },
        }
      }

      const type = field.type === 'phone' ? 'text' : field.type
      const schema = {
        $formkit: type,
        name: gfInputName(field.id),
        label: field.label,
        help: field.description || undefined,
        placeholder: field.placeholder,
        maxlength: field.maxLength || undefined,
        validation: validationTypes(
          field.isRequired,
          field.type,
          field.maxLength
        ),
        classes: {
          outer: fieldClass(field),
        },
      }

      if (needsOptions.has(type) && field.choices) {
        schema.options = selectOptions(field.choices)
      }

      return schema
    })

  return [
    {
      $el: 'div',
      attrs: { class: 'form-fields' },
      children: fields,
    },
    {
      $el: 'div',
      attrs: { class: 'form-actions' },
      children: [
        {
          $el: 'p',
          attrs: { class: 'required-key' },
          children: [
            { $el: 'span', attrs: { 'aria-hidden': 'true' }, children: ['*'] },
            ' Required',
          ],
        },
        {
          $formkit: 'submit',
          label: submitButton.value,
          disabled: disabled.value,
          classes: {
            outer: 'submit-field',
            input: 'form-submit-button',
          },
        },
      ],
    },
  ]
})

const submitButton = computed(() =>
  disabled.value ? 'Submitting…' : form.value?.button?.text || 'Submit'
)

const scrollToForm = () => {
  const target = document.getElementById('contact-form')
  if (!target) return

  if (locoScroll.value?.scrollTo) {
    locoScroll.value.scrollTo(target, {
      offset: -150,
      duration: 0.8,
      force: true,
    })
    return
  }

  const top = window.scrollY + target.getBoundingClientRect().top - 150
  window.scrollTo({ top, behavior: 'smooth' })
}

const getForm = async () => {
  isLoading.value = true
  loadError.value = ''

  try {
    const [formResponse, countriesResponse] = await Promise.all([
      Axios.get(`${siteUrl}/wp-json/wp/v2/forms/${props.data.form_id}`),
      Axios.get(`${siteUrl}/wp-json/wp/v2/gravity-form-countries`),
    ])

    if (!formResponse.data?.id) {
      throw new Error('The selected Gravity Form could not be found.')
    }

    form.value = formResponse.data
    countriesList.value = countriesResponse.data || []
  } catch (error) {
    loadError.value =
      error.response?.data?.message ||
      error.message ||
      'The form could not be loaded. Please try again later.'
  } finally {
    isLoading.value = false
  }
}

const submissionErrors = (response) => {
  const validationMessages = response?.validation_messages || {}

  return Object.values(validationMessages)
    .flatMap((message) => (Array.isArray(message) ? message : [message]))
    .filter(Boolean)
}

const postData = async (url, body) => {
  try {
    const response = await Axios.post(url, body)
    const result = response.data || {}

    if (!result.is_valid || !result.confirmation_message) {
      isSuccessful.value = false
      messages.value = submissionErrors(result)

      if (!messages.value.length) {
        messages.value = ['Please review the form and try again.']
      }

      return
    }

    isSuccessful.value = true
    messages.value = [result.confirmation_message]
    reset('gravity-form')
    isFormVisible.value = false
  } catch (error) {
    isSuccessful.value = false
    messages.value = [
      error.response?.data?.message ||
        'The form could not be submitted. Please try again later.',
    ]
  } finally {
    disabled.value = false
    scrollToForm()
  }
}

const submitHandler = (data) => {
  if (disabled.value || !form.value?.submitURL) return

  disabled.value = true
  messages.value = []
  postData(form.value.submitURL, data)
}

const showErrors = () => {
  isSuccessful.value = false
  messages.value = ['Please complete all required fields.']
  scrollToForm()
}

const showFormAgain = () => {
  isFormVisible.value = true
  isSuccessful.value = true
  messages.value = []
}

onBeforeMount(getForm)
</script>

<style lang="scss">
.contact-form-module-component {
  @apply w-full py-10 lg:py-[70px];

  .form-inner {
    @apply flex flex-col gap-5 lg:flex-row lg:items-start;
  }

  .form-group {
    @apply w-full overflow-hidden rounded-[15px] bg-white p-6 lg:p-10;
  }

  .form-section-title {
    @apply header-3 mb-8 text-balanced;
  }

  .form-status,
  .form-response {
    @apply body text-humble;
  }

  .form-error,
  .form-response.error,
  .formkit-message {
    @apply text-red-600;
  }

  .form-fields {
    @apply flex w-full flex-wrap lg:-mx-2;
  }

  .formkit-outer {
    @apply mb-6 w-full lg:px-2;

    &.half-width {
      @apply lg:w-1/2;
    }
  }

  .formkit-label,
  .formkit-legend {
    @apply mb-2 block text-[14px] leading-[18px] text-humble;
  }

  .required > .formkit-wrapper > .formkit-label,
  .required > .formkit-fieldset > .formkit-legend {
    &::after {
      content: '*';
      @apply ml-1 text-red-600;
    }
  }

  .formkit-help {
    @apply mb-2 text-[14px] leading-[18px] text-humble/70;
  }

  input:not([type='checkbox']):not([type='radio']),
  textarea,
  select {
    @apply w-full border-0 border-b border-balanced bg-transparent px-2 py-3 text-humble shadow-none outline-none placeholder:text-humble/50 focus:border-progressive focus:ring-0;
  }

  textarea {
    @apply min-h-[120px] resize-y;
  }

  .formkit-fieldset {
    @apply m-0 w-full border-0 p-0;
  }

  .formkit-options {
    @apply flex flex-col gap-3;
  }

  .formkit-option .formkit-wrapper,
  .consent-checkbox .formkit-wrapper {
    @apply flex items-start gap-3;
  }

  input[type='checkbox'],
  input[type='radio'] {
    @apply mt-1 h-5 w-5 flex-none border-balanced bg-transparent text-balanced focus:ring-progressive;
  }

  input[type='checkbox'] {
    @apply rounded-none;
  }

  .consent-field {
    @apply mb-6 w-full;

    .gfield_consent_description {
      @apply mb-4 text-[14px] leading-[18px] text-humble/80;

      p,
      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        @apply mb-3;
      }

      a {
        @apply underline;
      }
    }

    .consent-checkbox {
      @apply mb-0 px-0;
    }
  }

  .form-html {
    @apply body text-humble;
  }

  .formkit-messages {
    @apply mt-2 pl-0;
  }

  .form-actions {
    @apply flex flex-col items-start justify-between gap-5 pt-2 sm:flex-row sm:items-center;
  }

  .required-key {
    @apply order-2 m-0 text-[14px] leading-[18px] sm:order-1;

    span {
      @apply text-red-600;
    }
  }

  .submit-field {
    @apply order-1 m-0 w-auto p-0 sm:order-2;
  }

  .form-submit-button,
  .submit-another-button,
  .contact-card-button {
    @apply inline-flex w-fit cursor-pointer items-center justify-center rounded-full bg-progressive px-8 py-3 text-center text-[18px] leading-[22px] text-humble transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50;
  }

  .form-response {
    @apply mt-6;

    &:not(.error) {
      @apply text-balanced;
    }
  }

  .submit-another-button {
    @apply mt-6;
  }

  .contact-cards {
    @apply flex w-full flex-col gap-5 lg:w-[302px] lg:flex-[0_0_302px];
  }

  .contact-card {
    @apply flex min-h-[180px] flex-col justify-between gap-6 rounded-[15px] bg-balanced p-6;

    * {
      @apply text-white;
    }
  }

  .contact-card-title {
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    p {
      @apply header-5 normal-case;
    }
  }

  .contact-bottom {
    @apply flex flex-col items-start gap-5;
  }

  .contact-card-info {
    @apply body;

    a {
      @apply underline;
    }
  }

  .contact-card-button {
    @apply ml-auto bg-approachable text-humble;
  }
}
</style>
